import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Button, Space, Modal, Form, Input, Select, message, Tag,
  InputNumber, Card, Row, Col, Divider, Checkbox, Image, Typography
} from 'antd';
import { EyeOutlined, EditOutlined, PlusOutlined, ShoppingCartOutlined, HomeOutlined, DollarOutlined, CloseOutlined, CustomerServiceOutlined, CloudDownloadOutlined } from '@ant-design/icons';
import { orderAPI, dropshipperAPI, addressAPI, exchangeRateAPI, productAPI, walletAPI } from '../api';
import { normalizeMoney } from '../utils/money';
import countryList from 'react-select-country-list';
import { useNavigate } from 'react-router-dom';
import ShopifyIcon from './Utils/ShopifyIcon';
import WooCommerceIcon from './Utils/WooCommerceIcon';

const { Option } = Select;
const { TextArea } = Input;
const { Text, Paragraph } = Typography;
// 获取API基础URL
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

const getPlatformIcon = (platform) => {
  switch ((platform || '').toLowerCase()) {
    case 'shopify':
      return <ShopifyIcon />;
    case 'woocommerce':
      return <WooCommerceIcon />;
    default:
      return null; // 其他平台不显示图标
  }
};

const renderPlatformTag = (platform) => {
  if (!platform) {
    return '-';
  }

  let style = {};
  const lowerPlatform = (platform || '').toLowerCase();
  if (lowerPlatform === 'woocommerce') {
    style = { background: '#f3e5f5', borderColor: '#9c27b0', color: '#6a1b9a' };
  } else if (lowerPlatform === 'shopify') {
    style = { backgroundColor: '#f0f9f0', borderColor: '#96bf48', color: '#2d5a1d' };
  } else {
    style = { backgroundColor: '#ffffff', borderColor: '#d9d9d9', color: '#000000' };
  }

  return (
    <Tag
      icon={getPlatformIcon(platform)}
      style={style}
    >
      {platform.toUpperCase()}
    </Tag>
  );
};

const OrderManagement = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  // 分页相关状态
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [rmaModalVisible, setRmaModalVisible] = useState(false);
  const [warningModalVisible, setWarningModalVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [form] = Form.useForm();
  const [rmaForm] = Form.useForm();
  const [products, setProducts] = useState({}); // 缓存产品信息

  // 批量操作相关状态
  const [selectedOrderNumbers, setSelectedOrderNumbers] = useState([]); // 选中的订单号列表
  const [batchPayLoading, setBatchPayLoading] = useState(false);

  // Address management states
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [addressModalVisible, setAddressModalVisible] = useState(false);
  const [addressFormModalVisible, setAddressFormModalVisible] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [addressForm] = Form.useForm();
  const [modal, modalContextHolder] = Modal.useModal();

  // 获取国家列表数据
  const countries = countryList();
  const countryOptions = countries.getData();

  const fetchOrders = useCallback(async (
    page = pagination.current,
    pageSize = pagination.pageSize,
    searchValue = searchTerm
  ) => {
    try {
      setLoading(true);
      const trimmedSearch = searchValue?.trim();
      const params = { page, page_size: pageSize };
      if (trimmedSearch) {
        params.search = trimmedSearch;
      }
      const response = await orderAPI.getAll(params);

      // 处理分页响应
      const ordersData = response.data.data || [];
      const normalizedOrders = ordersData.map(order => ({
        ...order,
        total_amount: order.total_amount != null ? normalizeMoney(order.total_amount) : '0.00',
        price_details: (order.price_details || []).map(detail => ({
          ...detail,
          amount: detail?.amount != null ? normalizeMoney(detail.amount) : '0.00',
        })),
        items_detail: (order.items_detail || []).map(item => ({
          ...item,
          unit_price: item?.unit_price != null ? normalizeMoney(item.unit_price) : '0.00',
          customization_cost: item?.customization_cost != null ? normalizeMoney(item.customization_cost) : '0.00',
        })),
      }));
      setOrders(normalizedOrders);
      setPagination(prev => ({
        ...prev,
        current: response.data.page,
        pageSize: response.data.page_size,
        total: response.data.total,
      }));

      // 获取所有订单中涉及的产品信息
      const productIds = new Set();
      ordersData.forEach(order => {
        if (order.items_detail) {
          order.items_detail.forEach(item => {
            productIds.add(item.product_id);
          });
        }
      });

      // 批量获取产品信息
      const productPromises = Array.from(productIds).map(async (productId) => {
        try {
          const productResponse = await productAPI.getById(productId, { include_variant_images: true });
          return { id: productId, data: productResponse.data };
        } catch (error) {
          
          return { id: productId, data: null };
        }
      });

      const productResults = await Promise.all(productPromises);
      const productMap = {};
      productResults.forEach(result => {
        productMap[result.id] = result.data;
      });
      setProducts(productMap);

    } catch (error) {
      message.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize, searchTerm]);

  useEffect(() => {
    fetchOrders();
    fetchSavedAddresses();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearchInputChange = (event) => {
    const value = event.target.value ?? '';
    setSearchTerm(value);
    if (value === '') {
      setPagination(prev => ({
        ...prev,
        current: 1,
      }));
      fetchOrders(1, pagination.pageSize, '');
    }
  };

  const handleSearchOrders = (value) => {
    const normalizedValue = value ?? '';
    setSearchTerm(normalizedValue);
    setPagination(prev => ({
      ...prev,
      current: 1,
    }));
    fetchOrders(1, pagination.pageSize, normalizedValue);
  };

  const fetchSavedAddresses = async () => {
    try {
      const response = await addressAPI.getAll();
      setSavedAddresses(response.data);

      // Set default address if exists and no address is currently selected
      if (!selectedAddressId) {
        const defaultAddress = response.data.find(addr => addr.is_default);
        if (defaultAddress) {
          setSelectedAddressId(defaultAddress.id);
        }
      }
    } catch (error) {
      
    }
  };

  // 处理分页、排序、筛选变化
  const handleTableChange = (paginationConfig, filters, sorter) => {
    const { current, pageSize } = paginationConfig;
    // 切换页面时清除选中状态
    setSelectedOrderNumbers([]);
    fetchOrders(current, pageSize, searchTerm);
  };

  // 批量操作辅助函数
  const handleSelectOrder = (orderNumber, checked) => {
    if (checked) {
      setSelectedOrderNumbers([...selectedOrderNumbers, orderNumber]);
    } else {
      setSelectedOrderNumbers(selectedOrderNumbers.filter(num => num !== orderNumber));
    }
  };

  const handleSelectAllInPage = (checked) => {
    if (checked) {
      const currentPageOrderNumbers = orders.map(o => o.order_number);
      setSelectedOrderNumbers(currentPageOrderNumbers);
    } else {
      setSelectedOrderNumbers([]);
    }
  };

  const isAllInPageSelected = () => {
    if (orders.length === 0) return false;
    return orders.every(o => selectedOrderNumbers.includes(o.order_number));
  };

  const isSomeInPageSelected = () => {
    if (orders.length === 0) return false;
    return orders.some(o => selectedOrderNumbers.includes(o.order_number)) && !isAllInPageSelected();
  };

  // 批量支付
  const handleBatchPay = async () => {
    try {
      setBatchPayLoading(true);

      // Filter only AWAITING_PAYMENT orders
      const selectedOrders = orders.filter(o =>
        selectedOrderNumbers.includes(o.order_number) && o.status === 'AWAITING_PAYMENT'
      );

      if (selectedOrders.length === 0) {
        message.warning('No unpaid orders selected');
        return;
      }

      // Calculate total amount for selected orders
      const totalAmount = selectedOrders.reduce((sum, order) => {
        return sum + parseFloat(order.total_amount || '0');
      }, 0);

      // Check wallet balance
      const walletResponse = await walletAPI.getMyWallet();
      const walletBalance = parseFloat(normalizeMoney(walletResponse.data.balance_minor || '0'));

      if (walletBalance >= totalAmount) {
        // Sufficient balance - show confirmation
        const remainingBalance = walletBalance - totalAmount;
        modal.confirm({
          title: 'Confirm Payment with Wallet Balance',
          content: (
            <div>
              <p><strong>Total Amount:</strong> ${totalAmount.toFixed(2)}</p>
              <p><strong>Current Balance:</strong> ${walletBalance.toFixed(2)}</p>
              <p><strong>Remaining Balance:</strong> ${remainingBalance.toFixed(2)}</p>
              <p style={{ marginTop: 16 }}>Do you want to proceed with the payment?</p>
            </div>
          ),
          onOk: async () => {
            await proceedWithBatchPayment(selectedOrders.map(o => o.order_number));
          },
          okText: 'Confirm Payment',
          okButtonProps: { style: { backgroundColor: '#ff8c00', borderColor: '#ff8c00' } }
        });
      } else {
        // Insufficient balance - check order count limit
        if (selectedOrders.length > 20) {
          message.error('Cannot process more than 20 orders at once. Please select 20 or fewer orders.');
          return;
        }

        // Navigate to top-up
        const shortfall = totalAmount - walletBalance;
        const orderItems = selectedOrders.map(order => {
          const firstItem = order.items_detail && order.items_detail.length > 0 ? order.items_detail[0] : null;
          const product = firstItem ? products[firstItem.product_id] : null;
          let productName = product ? (product.name.length > 80 ? product.name.substring(0, 80) + '...' : product.name) : `Order #${order.order_number}`;
          if (order.items_detail && order.items_detail.length > 1) {
            productName += ` (+${order.items_detail.length - 1} more items)`;
          }
          return {
            order_number: order.order_number,
            product_name: productName
          };
        });

        navigate('/dropshipper/my-wallet', {
          state: {
            paymentContext: {
              orders: orderItems,
              amount: shortfall,
              autoPayAfterTopUp: true
            }
          }
        });
      }
    } catch (error) {
      if (error.response?.data?.detail) {
        message.error(`Payment check failed: ${error.response.data.detail}`);
      } else {
        message.error('Payment check failed');
      }
    } finally {
      setBatchPayLoading(false);
    }
  };

  const proceedWithBatchPayment = async (orderNumbers) => {
    try {
      const response = await orderAPI.pay(orderNumbers);

      // 显示批量支付结果
      const { success_count, failed_orders } = response.data;

      if (failed_orders && failed_orders.length > 0) {
        const failedMessages = failed_orders.map(f => `Order ${f.order_number}: ${f.error}`).join('\n');
        Modal.warning({
          title: 'Batch Payment Completed with Errors',
          content: (
            <div>
              <p>Successfully paid: {success_count} orders</p>
              <p>Failed: {failed_orders.length} orders</p>
              <div style={{ marginTop: 10, maxHeight: 200, overflow: 'auto' }}>
                <pre style={{ fontSize: 12 }}>{failedMessages}</pre>
              </div>
            </div>
          ),
          width: 600
        });
      } else {
        message.success(`Successfully paid ${success_count} orders`);
      }

      setSelectedOrderNumbers([]);
      fetchOrders();
    } catch (error) {
      if (error.response?.data?.detail) {
        message.error(`Batch payment failed: ${error.response.data.detail}`);
      } else {
        message.error('Batch payment failed');
      }
    }
  };

  const handleViewDetail = (record) => {
    navigate(`/dropshipper/orders/${record.order_number}`);
  };

  const handleEditStatus = (record) => {
    setSelectedOrder(record);
    form.setFieldsValue({
      status: record.status,
      tracking_number: record.tracking_number || ''
    });
    setEditModalVisible(true);
  };

  const handleUpdateStatus = async (values) => {
    try {
      await orderAPI.update(selectedOrder.order_number, values);
      message.success('Status updated successfully');
      setEditModalVisible(false);
      fetchOrders();
    } catch (error) {
      message.error('Status update failed');
    }
  };

  // 支付订单（单个）
  const handlePayOrder = async (orderNumber) => {
    try {
      const order = orders.find(o => o.order_number === orderNumber);
      if (!order) {
        message.error('Order not found');
        return;
      }

      const totalAmount = parseFloat(order.total_amount || '0');

      // Check wallet balance
      const walletResponse = await walletAPI.getMyWallet();
      const walletBalance = parseFloat(normalizeMoney(walletResponse.data.balance_minor || '0'));

      if (walletBalance >= totalAmount) {
        // Sufficient balance - show confirmation
        const remainingBalance = walletBalance - totalAmount;
        modal.confirm({
          title: 'Confirm Payment with Wallet Balance',
          content: (
            <div>
              <p><strong>Order:</strong> #{orderNumber}</p>
              <p><strong>Amount:</strong> ${totalAmount.toFixed(2)}</p>
              <p><strong>Current Balance:</strong> ${walletBalance.toFixed(2)}</p>
              <p><strong>Remaining Balance:</strong> ${remainingBalance.toFixed(2)}</p>
              <p style={{ marginTop: 16 }}>Do you want to proceed with the payment?</p>
            </div>
          ),
          onOk: async () => {
            await proceedWithSinglePayment(orderNumber);
          },
          okText: 'Confirm Payment',
          okButtonProps: { style: { backgroundColor: '#ff8c00', borderColor: '#ff8c00' } }
        });
      } else {
        // Insufficient balance - navigate to top-up
        const shortfall = totalAmount - walletBalance;
        const firstItem = order.items_detail && order.items_detail.length > 0 ? order.items_detail[0] : null;
        const product = firstItem ? products[firstItem.product_id] : null;
        let productName = product ? (product.name.length > 80 ? product.name.substring(0, 80) + '...' : product.name) : `Order #${orderNumber}`;
        if (order.items_detail && order.items_detail.length > 1) {
          productName += ` (+${order.items_detail.length - 1} more items)`;
        }

        navigate('/dropshipper/my-wallet', {
          state: {
            paymentContext: {
              orders: [{
                order_number: orderNumber,
                product_name: productName
              }],
              amount: shortfall,
              autoPayAfterTopUp: true
            }
          }
        });
      }
    } catch (error) {
      if (error.response?.data?.detail) {
        message.error(`Payment check failed: ${error.response.data.detail}`);
      } else {
        message.error('Payment check failed');
      }
    }
  };

  const proceedWithSinglePayment = async (orderNumber) => {
    try {
      const response = await orderAPI.pay(orderNumber);

      // 检查支付结果
      if (response.data.failed_count > 0) {
        const error = response.data.failed_orders[0].error;
        message.error(`Payment failed: ${error}`);
      } else {
        message.success('Order paid successfully');
      }
      fetchOrders();
    } catch (error) {
      if (error.response?.data?.detail) {
        message.error(`Payment failed: ${error.response.data.detail}`);
      } else {
        message.error('Payment failed');
      }
    }
  };

  // 取消订单
  const handleCancelOrder = async (orderNumber) => {
    try {
      await orderAPI.cancel(orderNumber);
      message.success('Order cancelled successfully');
      fetchOrders();
    } catch (error) {
      message.error('Cancel order failed');
    }
  };

  // RMA申请
  const handleRequestRMA = (record) => {
    setSelectedOrder(record);

    // 初始化RMA表单，所有商品都可以申请售后（包括未发货的）
    const eligibleItems = record.items_detail?.map((item, index) => {
      // 只要不是已经在RMA流程中的商品都可以申请
      if (!item.rma_status) {
        return {
          item_index: index,
          rma_comment: '',
          selected: false,
          product_id: item.product_id,
          variant_path: item.variant_path,
          quantity: item.quantity,
          fulfillment_status: item.fulfillment_status
        };
      }
      return null;
    }).filter(Boolean) || [];

    if (eligibleItems.length === 0) {
      message.warning('All items are already in RMA process');
      return;
    }

    rmaForm.setFieldsValue({ items: eligibleItems });
    setRmaModalVisible(true);
  };

  const handleSubmitRMA = async (values) => {
    try {
      const selectedItems = values.items.filter(item => item.selected);
      if (selectedItems.length === 0) {
        message.error('Please select at least one item for RMA');
        return;
      }
      await orderAPI.requestRMA(selectedOrder.order_number, selectedItems);
      message.success('RMA request submitted successfully');
      setRmaModalVisible(false);
      fetchOrders();
    } catch (error) {
      message.error('RMA request failed');
    }
  };

  // 编辑订单备注
  // 显示警告信息
  const handleShowWarning = (record) => {
    setSelectedOrder(record);
    setWarningModalVisible(true);
  };

  // 清除警告
  const handleClearWarning = async () => {
    try {
      await orderAPI.clearWarning(selectedOrder.order_number);
      message.success('Warning cleared successfully');
      setWarningModalVisible(false);

      // 更新selectedOrder状态以立即反映变化
      setSelectedOrder({
        ...selectedOrder,
        warning: []
      });

      fetchOrders();
    } catch (error) {
      if (error.response?.data?.detail) {
        message.error(error.response.data.detail);
      } else {
        message.error('Failed to clear warning');
      }
    }
  };






  const getStatusColor = (status) => {
    const colors = {
      'AWAITING_PAYMENT': 'blue',
      'CONFIRMED': 'blue',
      'CANCELLED': 'red',
      'CLOSED': 'green',
    };
    return colors[status] || 'default';
  };

  const getStatusText = (status) => {
    const texts = {
      'AWAITING_PAYMENT': 'Awaiting Payment',
      'CONFIRMED': 'Paid',
      'CANCELLED': 'Cancelled',
      'CLOSED': 'Completed',
    };
    return texts[status] || status;
  };

  const getFulfillmentStatusColor = (status) => {
    const colors = {
      'AWAITING': 'blue',
      'ACCEPTED': 'blue',
      'REJECTED': 'red',
      'ON_HOLD': 'yellow',
      'SHIPPED': 'green',
      'DELIVERED': 'cyan',
    };
    return colors[status] || 'default';
  };

  const getFulfillmentStatusText = (status) => {
    const texts = {
      'AWAITING': 'Awaiting',
      'ACCEPTED': 'Accepted',
      'REJECTED': 'Rejected',
      'ON_HOLD': 'On Hold',
      'SHIPPED': 'Shipped',
      'DELIVERED': 'Delivered',
    };
    return texts[status] || status;
  };

  const getRMAStatusColor = (status) => {
    const colors = {
      'RMA_REQUESTED': 'blue',
      'RMA_AUTHORIZED': 'blue',
      'RMA_IN_TRANSIT': 'yellow',
      'RMA_RECEIVED': 'green',
      'REFUND_COMPLETED': 'cyan',
    };
    return colors[status] || 'default';
  };

  const getRMAStatusText = (status) => {
    const texts = {
      'RMA_REQUESTED': 'RMA Requested',
      'RMA_AUTHORIZED': 'RMA Authorized',
      'RMA_IN_TRANSIT': 'In Transit',
      'RMA_RECEIVED': 'Received',
      'REFUND_COMPLETED': 'Refund Completed',
    };
    return texts[status] || status;
  };

  // 获取变体信息的辅助函数
  const getVariantInfo = (variants, variantPath) => {
    if (!variants || !Array.isArray(variants)) return null;

    if (!variantPath || variantPath.length === 0) {
      // 单品情况，查找没有variant_path或variant_path为空的项
      for (const variant of variants) {
        if (!variant.variant_path || variant.variant_path.length === 0) {
          return variant;
        }
      }
      return null;
    } else {
      // 多变体情况，根据variant_path查找匹配的项
      for (const variant of variants) {
        const variantVariantPath = variant.variant_path || [];
        if (JSON.stringify(variantVariantPath) === JSON.stringify(variantPath)) {
          return variant;
        }
      }
      return null;
    }
  };

  // 获取产品封面图片URL
  const getProductCoverImage = (product) => {
    if (!product?.images?.length) {
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjEwIiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+Tm8gSW1hZ2U8L3RleHQ+PC9zdmc+';
    }

    // 优先显示非变体图片作为封面
    const coverImage = product.images.find(img => !img.for_variants) || product.images[0];
    return `${API_BASE_URL}/products/${product.id}/images/${coverImage.id}`;
  };

  // 获取变体图片URL
  const getVariantImage = (product, variantPath) => {
    if (!product?.images?.length || !variantPath || variantPath.length === 0) return null;

    const variantInfo = getVariantInfo(product.variants, variantPath);
    if (!variantInfo?.image) return null;

    return `${API_BASE_URL}/products/${product.id}/images/${variantInfo.image}`;
  };

  // 处理产品名称点击跳转 - 在新窗口打开
  const handleProductClick = (productId) => {
    window.open(`/product/${productId}`, '_blank');
  };

  // 获取订单整体fulfillment状态摘要
  const getFulfillmentSummary = (items_detail, warning, record) => {
    if (!items_detail || items_detail.length === 0) return 'No Items';

    const statusCounts = {};
    let hasAbnormal = false;

    items_detail.forEach(item => {
      const status = item.fulfillment_status || 'AWAITING';
      statusCounts[status] = (statusCounts[status] || 0) + 1;

      if (['ON_HOLD', 'REJECTED'].includes(status)) {
        hasAbnormal = true;
      }
    });

    let statusDisplay;

    // 如果所有商品状态相同
    const statuses = Object.keys(statusCounts);
    if (statuses.length === 1) {
      const status = statuses[0];
      if (hasAbnormal) {
        statusDisplay = <Tag color="red">Abnormal</Tag>;
      } else {
        statusDisplay = <Tag color={getFulfillmentStatusColor(status)}>{getFulfillmentStatusText(status)}</Tag>;
      }
    } else if (hasAbnormal) {
      // 如果有异常状态，优先显示
      statusDisplay = <Tag color="red">Abnormal</Tag>;
    } else {
      // 显示状态计数
      statusDisplay = (
        <div>
          {Object.entries(statusCounts).map(([status, count]) => (
            <div key={status} style={{ fontSize: '12px' }}>
              {count}: {getFulfillmentStatusText(status)}
            </div>
          ))}
        </div>
      );
    }

    // 检查是否有警告信息
    const hasWarnings = warning && Array.isArray(warning) && warning.length > 0;

    return (
      <div>
        {statusDisplay}
        {hasWarnings && (
          <div style={{ marginTop: '4px' }}>
            <Button
              size="small"
              type="primary"
              danger
              onClick={() => handleShowWarning(record)}
              style={{
                backgroundColor: '#faad14',
                borderColor: '#faad14',
                fontSize: '10px',
                height: '20px',
                padding: '0 6px'
              }}
            >
              ⚠️ WARNING ({warning.length}) ⚠️
            </Button>
          </div>
        )}
      </div>
    );
  };


  // Address management handlers
  const handleAddressManagement = () => {
    setAddressModalVisible(true);
  };

  const handleAddressSelect = (addressId) => {
    setSelectedAddressId(addressId);
  };

  const handleNewAddress = () => {
    setEditingAddress(null);
    addressForm.resetFields();
    setAddressFormModalVisible(true);
  };

  const handleEditAddress = () => {
    if (selectedAddressId) {
      const address = savedAddresses.find(addr => addr.id === selectedAddressId);
      if (address) {
        setEditingAddress(address);
        addressForm.setFieldsValue(address);
        setAddressFormModalVisible(true);
      }
    }
  };

  const handleAddressSubmit = async (values) => {
    try {
      if (editingAddress) {
        await addressAPI.update(editingAddress.id, values);
        message.success('Address updated successfully');

      } else {
        await addressAPI.create(values);
        message.success('Address created successfully');
      }
      setAddressFormModalVisible(false);
      addressForm.resetFields();
      fetchSavedAddresses();
    } catch (error) {
      message.error('Failed to save address');
    }
  };

  const handleDeleteAddress = async (addressId) => {
    try {
      await addressAPI.delete(addressId);
      message.success('Address deleted successfully');
      if (selectedAddressId === addressId) {
        setSelectedAddressId(null);
      }
      fetchSavedAddresses();
    } catch (error) {
      message.error('Failed to delete address');
    }
  };

  const handleSetDefaultAddress = async (addressId) => {
    try {
      await addressAPI.setDefault(addressId);
      message.success('Default address updated');
      fetchSavedAddresses();
    } catch (error) {
      message.error('Failed to set default address');
    }
  };

  const columns = [
    {
      title: (
        <Checkbox
          checked={isAllInPageSelected()}
          indeterminate={isSomeInPageSelected()}
          onChange={(e) => handleSelectAllInPage(e.target.checked)}
        />
      ),
      key: 'selection',
      width: 50,
      fixed: 'left',
      render: (_, record) => (
        <Checkbox
          checked={selectedOrderNumbers.includes(record.order_number)}
          onChange={(e) => handleSelectOrder(record.order_number, e.target.checked)}
        />
      ),
    },
    {
      title: 'Product Overview',
      key: 'products',
      width: '22%',
      render: (_, record) => {
        if (record.items_detail && record.items_detail.length > 0) {
          const firstItem = record.items_detail[0];
          const product = products[firstItem.product_id];

          if (!product) {
            return `Product ID: ${firstItem.product_id}`;
          }

          const coverImageUrl = getProductCoverImage(product);
          const variantImageUrl = getVariantImage(product, firstItem.variant_path);

          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {/* 优先显示变体图片，如果没有变体图片则显示封面图片 */}
              <Image
                src={variantImageUrl || coverImageUrl}
                alt={variantImageUrl ? "Variant image" : "Product cover"}
                width={40}
                height={40}
                style={{
                  objectFit: 'cover',
                  borderRadius: '4px',
                  border: variantImageUrl ? '2px solid #2557ff' : 'none',
                  cursor: 'pointer'
                }}
                onError={(e) => {
                  // 如果变体图片加载失败，fallback到封面图片
                  if (variantImageUrl && e.target.src === variantImageUrl) {
                    e.target.src = coverImageUrl;
                    e.target.style.border = 'none';
                  } else {
                    // 如果封面图片也失败，显示占位符
                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjgiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5OL0E8L3RleHQ+PC9zdmc+';
                    e.target.style.border = 'none';
                  }
                }}
              />

              <div style={{ flex: 1, minWidth: 0 }}>
                <Paragraph
                  ellipsis={{
                    rows: 3,
                    tooltip: product.name,
                  }}
                  style={{
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    color: '#1890ff',
                    textDecoration: 'none',
                    margin: 0,
                  }}
                  onClick={() => handleProductClick(firstItem.product_id)}
                  onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                  onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                >
                  {product.name}
                </Paragraph>
                {record.items_detail.length > 1 && (
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    +{record.items_detail.length - 1} more items
                  </div>
                )}
              </div>
            </div>
          );
        }
        return '-';
      },
    },
    {
      title: 'Channel',
      dataIndex: 'channel',
      key: 'channel',
      width: 160,
      render: (channel) => renderPlatformTag(channel),
    },
    {
      title: 'Customer Name',
      dataIndex: 'customer_name',
      key: 'customer_name',
      width: '10%',
      ellipsis: true,
    },
    {
      title: 'Order Amount',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (amount) => `$${normalizeMoney(amount ?? '0')}`,
      width: '8%',
    },
    {
      title: 'Order Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
      width: '10%',
    },
    {
      title: 'Fulfillment Status',
      key: 'fulfillment_status',
      render: (_, record) => getFulfillmentSummary(record.items_detail, record.warning, record),
      width: '11%',
    },
    {
      title: 'Created Time',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleString(),
      width: '11%',
    },
    {
      title: 'Actions',
      key: 'action',
      width: '13%',
      render: (_, record) => (
        <Space size="small" wrap>
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
            style={{ backgroundColor: '#ff8c00', borderColor: '#ff8c00' }}
          >
            Details
          </Button>

          {record.status === 'AWAITING_PAYMENT' && (
            <>
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => navigate(`/dropshipper/edit-order/${record.order_number}`)}
                style={{ backgroundColor: '#52c41a', borderColor: '#52c41a', color: 'white' }}
              >
                Edit
              </Button>
              <Button
                type="primary"
                size="small"
                icon={<DollarOutlined />}
                onClick={() => {
                  modal.confirm({
                    title: 'Confirm Payment',
                    content: 'Are you sure you want to pay this order?',
                    onOk: () => handlePayOrder(record.order_number),
                  });
                }}
              >
                Pay
              </Button>
              <Button
                danger
                size="small"
                icon={<CloseOutlined />}
                onClick={() => {
                  modal.confirm({
                    title: 'Cancel Order',
                    content: 'Are you sure you want to cancel this order?',
                    onOk: () => handleCancelOrder(record.order_number),
                  });
                }}
              >
                Cancel
              </Button>
            </>
          )}

          {record.status === 'CONFIRMED' && (
            <Button
              size="small"
              icon={<CustomerServiceOutlined />}
              onClick={() => handleRequestRMA(record)}
            >
              RMA Request
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      {modalContextHolder}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 'bold' }}>
          <ShoppingCartOutlined style={{ marginRight: 8 }} />
          My Orders
        </h1>
        <Space>
          <Button
            icon={<CloudDownloadOutlined />}
            onClick={() => navigate('/dropshipper/my-stores', { state: { importGuide: true } })}
          >
            Import orders from my store
          </Button>
          <Button
            icon={<HomeOutlined />}
            onClick={handleAddressManagement}
          >
            My Address
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/dropshipper/create-order')}
            style={{ backgroundColor: '#ff8c00', borderColor: '#ff8c00' }}
          >
            Add Order
          </Button>
        </Space>
      </div>

      <div style={{ marginBottom: 16, maxWidth: 360 }}>
        <Input.Search
          allowClear
          enterButton="Search"
          placeholder="Search orders by order info, channel ID, or product"
          value={searchTerm}
          onChange={handleSearchInputChange}
          onSearch={handleSearchOrders}
        />
      </div>

      {/* 批量操作栏 */}
      {selectedOrderNumbers.length > 0 && (
        <div style={{
          marginBottom: 16,
          padding: '12px 16px',
          backgroundColor: '#e6f7ff',
          borderRadius: '4px',
          border: '1px solid #91d5ff'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Text strong>
                Selected {selectedOrderNumbers.length} order(s)
              </Text>
            </div>
            <Space>
              <Button
                type="primary"
                icon={<DollarOutlined />}
                onClick={() => {
                  // 过滤出只有AWAITING_PAYMENT状态的订单
                  const selectedOrders = orders.filter(o => selectedOrderNumbers.includes(o.order_number));
                  const awaitingPaymentOrders = selectedOrders.filter(o => o.status === 'AWAITING_PAYMENT');

                  if (awaitingPaymentOrders.length === 0) {
                    message.warning('No unpaid orders selected');
                    return;
                  }

                  if (awaitingPaymentOrders.length < selectedOrders.length) {
                    modal.confirm({
                      title: 'Batch Payment Confirmation',
                      content: `Only ${awaitingPaymentOrders.length} out of ${selectedOrders.length} selected orders are unpaid. Proceed to pay these orders?`,
                      onOk: handleBatchPay,
                    });
                  } else {
                    modal.confirm({
                      title: 'Batch Payment Confirmation',
                      content: `Are you sure you want to pay ${awaitingPaymentOrders.length} selected order(s)?`,
                      onOk: handleBatchPay,
                    });
                  }
                }}
                loading={batchPayLoading}
              >
                Batch Pay
              </Button>
              <Button
                type="link"
                onClick={() => setSelectedOrderNumbers([])}
              >
                Cancel Selection
              </Button>
            </Space>
          </div>
        </div>
      )}

      <Table
        columns={columns}
        dataSource={orders}
        rowKey="order_number"
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        size="middle"
      />

      {/* RMA Request Modal */}
      <Modal
        title="Request RMA"
        open={rmaModalVisible}
        onCancel={() => setRmaModalVisible(false)}
        onOk={() => rmaForm.submit()}
        okText="Submit RMA Request"
        okButtonProps={{ style: { backgroundColor: '#ff8c00', borderColor: '#ff8c00' } }}
        cancelText="Cancel"
        width={800}
      >
        <Form
          form={rmaForm}
          layout="vertical"
          onFinish={handleSubmitRMA}
        >
          <Form.List name="items">
            {(fields) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Card key={key} size="small" style={{ marginBottom: 16 }}>
                    <Row gutter={16}>
                      <Col span={4}>
                        <Form.Item
                          {...restField}
                          name={[name, 'selected']}
                          valuePropName="checked"
                        >
                          <Checkbox>Select</Checkbox>
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <div style={{ padding: '5px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {(() => {
                            const productId = rmaForm.getFieldValue(['items', name, 'product_id']);
                            const variantPath = rmaForm.getFieldValue(['items', name, 'variant_path']);
                            const product = products[productId];
                            const coverImageUrl = product ? getProductCoverImage(product) : null;
                            const variantImageUrl = product ? getVariantImage(product, variantPath) : null;

                            return (
                              <>
                                <div style={{ display: 'flex', justifyContent: 'center' }}>
                                  <Image
                                    src={variantImageUrl || coverImageUrl}
                                    alt={variantImageUrl ? "Variant image" : "Product cover"}
                                    width={40}
                                    height={40}
                                    style={{
                                      objectFit: 'cover',
                                      borderRadius: '4px',
                                      border: variantImageUrl ? '2px solid #2557ff' : 'none',
                                      cursor: 'pointer'
                                    }}
                                    onError={(e) => {
                                      if (variantImageUrl && e.target.src === variantImageUrl && coverImageUrl) {
                                        e.target.src = coverImageUrl;
                                        e.target.style.border = 'none';
                                      } else {
                                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjgiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5OL0E8L3RleHQ+PC9zdmc+';
                                        e.target.style.border = 'none';
                                      }
                                    }}
                                  />
                                </div>
                                <div>
                                  <div
                                    style={{
                                      fontWeight: 'bold',
                                      cursor: 'pointer',
                                      color: '#1890ff'
                                    }}
                                    onClick={() => handleProductClick(productId)}
                                    onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                    onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                  >
                                    {product ? product.name : `Product ID: ${productId}`}
                                  </div>
                                  <div style={{ fontSize: '12px', color: '#666' }}>
                                    Quantity: {rmaForm.getFieldValue(['items', name, 'quantity'])}
                                  </div>
                                  {variantPath && variantPath.length > 0 && (
                                    <div style={{ fontSize: '12px', color: '#666' }}>
                                      Variant: {variantPath.join(' - ')}
                                    </div>
                                  )}
                                  {rmaForm.getFieldValue(['items', name, 'sku']) && (
                                    <div style={{ fontSize: '12px', color: '#666' }}>
                                      SKU: {rmaForm.getFieldValue(['items', name, 'sku'])}
                                    </div>
                                  )}
                                  <div style={{ fontSize: '12px', marginTop: 4 }}>
                                    <Tag color={getFulfillmentStatusColor(rmaForm.getFieldValue(['items', name, 'fulfillment_status']))}>
                                      {getFulfillmentStatusText(rmaForm.getFieldValue(['items', name, 'fulfillment_status']) || 'AWAITING')}
                                    </Tag>
                                  </div>
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          {...restField}
                          name={[name, 'rma_comment']}
                          label="RMA Reason"
                          rules={[
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                const selected = getFieldValue(['items', name, 'selected']);
                                if (selected && !value) {
                                  return Promise.reject(new Error('Please provide RMA reason for selected item'));
                                }
                                return Promise.resolve();
                              },
                            }),
                          ]}
                        >
                          <TextArea rows={2} placeholder="Please describe the reason for RMA" />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                ))}
              </>
            )}
          </Form.List>
        </Form>
      </Modal>

      {/* Address Management Modal */}
      <Modal
        title="My Addresses"
        open={addressModalVisible}
        onCancel={() => setAddressModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setAddressModalVisible(false)}>
            Close
          </Button>
        ]}
        width={800}
      >
        <div style={{ marginBottom: 16 }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleNewAddress}
            style={{ backgroundColor: '#ff8c00', borderColor: '#ff8c00' }}
          >
            Add New Address
          </Button>
        </div>

        <Table
          dataSource={savedAddresses}
          rowKey="id"
          pagination={false}
          size="small"
          columns={[
            {
              title: 'Name',
              dataIndex: 'name',
              key: 'name',
            },
            {
              title: 'Customer',
              dataIndex: 'customer_name',
              key: 'customer_name',
            },
            {
              title: 'Country',
              dataIndex: 'country',
              key: 'country',
              render: (country) => countryOptions.find(c => c.value === country)?.label || country,
            },
            {
              title: 'Address',
              dataIndex: 'address',
              key: 'address',
              ellipsis: true,
            },
            {
              title: 'Default',
              dataIndex: 'is_default',
              key: 'is_default',
              render: (isDefault) => isDefault ? <Tag color="green">Default</Tag> : null,
            },
            {
              title: 'Actions',
              key: 'actions',
              render: (_, record) => (
                <Space size="small">
                  <Button
                    size="small"
                    onClick={() => {
                      setEditingAddress(record);
                      addressForm.setFieldsValue(record);
                      setAddressFormModalVisible(true);
                    }}
                  >
                    Edit
                  </Button>
                  {!record.is_default && (
                    <Button
                      size="small"
                      onClick={() => handleSetDefaultAddress(record.id)}
                    >
                      Set Default
                    </Button>
                  )}
                  <Button
                    size="small"
                    danger
                    onClick={() => {
                      modal.confirm({
                        title: 'Delete Address',
                        content: 'Are you sure you want to delete this address?',
                        onOk: () => handleDeleteAddress(record.id),
                      });
                    }}
                  >
                    Delete
                  </Button>
                </Space>
              ),
            },
          ]}
        />
      </Modal>

      {/* Address Form Modal */}
      <Modal
        title={editingAddress ? 'Edit Address' : 'Add New Address'}
        open={addressFormModalVisible}
        onCancel={() => {
          setAddressFormModalVisible(false);
          addressForm.resetFields();
          setEditingAddress(null);
        }}
        onOk={() => addressForm.submit()}
        okText={editingAddress ? 'Update' : 'Create'}
        okButtonProps={{ style: { backgroundColor: '#ff8c00', borderColor: '#ff8c00' } }}
      >
        <Form
          form={addressForm}
          layout="vertical"
          onFinish={handleAddressSubmit}
        >
          <Form.Item
            label="Address Name/Label"
            name="name"
            rules={[{ required: true, message: 'Please enter address name' }]}
          >
            <Input placeholder="e.g., Home, Office, Warehouse" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Customer Name"
                name="customer_name"
                rules={[{ required: true, message: 'Please enter customer name' }]}
              >
                <Input placeholder="Enter customer name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Customer Phone"
                name="customer_phone"
                rules={[{ required: true, message: 'Please enter customer phone' }]}
              >
                <Input placeholder="Enter customer phone" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Customer Email (Optional)"
            name="customer_email"
            rules={[
              { type: 'email', message: 'Please enter valid email' }
            ]}
          >
            <Input placeholder="Enter customer email (optional)" />
          </Form.Item>

          <Form.Item
            label="Country/Region"
            name="country"
            rules={[{ required: true, message: 'Please select country' }]}
          >
            <Select
              placeholder="Select country"
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            >
              {countryOptions.map(country => (
                <Option key={country.value} value={country.value} label={country.label}>
                  {country.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Address"
            name="address"
            rules={[{ required: true, message: 'Please enter address' }]}
          >
            <Input.TextArea
              placeholder="Enter detailed address"
              rows={3}
            />
          </Form.Item>

          <Form.Item
            name="is_default"
            valuePropName="checked"
          >
            <Checkbox>
              Set as default address
            </Checkbox>
          </Form.Item>
        </Form>
      </Modal>

      {/* Warning Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: '#faad14', fontSize: '18px' }}>⚠️</span>
            Order Warning
          </div>
        }
        open={warningModalVisible}
        onCancel={() => setWarningModalVisible(false)}
        onOk={handleClearWarning}
        okText="Confirm"
        okButtonProps={{
          style: { backgroundColor: '#faad14', borderColor: '#faad14' },
          danger: true
        }}
        cancelText="Cancel"
        width={600}
      >
        <div style={{
          padding: '16px',
          backgroundColor: '#fff7e6',
          borderRadius: '6px',
          border: '1px solid #ffd591',
          marginBottom: '16px'
        }}>
          {selectedOrder?.warning && Array.isArray(selectedOrder.warning) && selectedOrder.warning.length > 0 ? (
            <div>
              {selectedOrder.warning.map((warning, index) => (
                <div key={index} style={{
                  marginBottom: index < selectedOrder.warning.length - 1 ? '12px' : '0',
                  paddingBottom: index < selectedOrder.warning.length - 1 ? '12px' : '0',
                  borderBottom: index < selectedOrder.warning.length - 1 ? '1px solid #ffd591' : 'none'
                }}>
                  <div style={{
                    fontSize: '12px',
                    color: '#666',
                    fontStyle: 'italic'
                  }}>
                    {new Date(warning.time * 1000).toLocaleString()}:
                  </div>
                  <div style={{
                    fontSize: '14px',
                    color: '#d46b08',
                    whiteSpace: 'pre-wrap',
                    lineHeight: '1.5',
                    marginBottom: '4px'
                  }}>
                    {warning.message}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{
              fontSize: '14px',
              color: '#d46b08',
              fontStyle: 'italic'
            }}>
              No warnings available
            </div>
          )}
        </div>

      </Modal>
    </div>
  );
};

export default OrderManagement;
